import { ROOM_CHATS_NODE } from "@lifeat/shared/rooms/RoomsRtdbNodes";
import {
    CHAT_MESSAGE_LENGTH_LIMIT,
    MESSAGES_TO_FETCH_PAGE_SIZE,
} from "@lifeat/shared/rooms/room-chat/RoomChatConstants";
import {
    ClearAllMessagePayload,
    DeleteMessagePayload,
    ReportMessagePayload,
} from "@lifeat/shared/rooms/room-chat/RoomChatFunctionTypes";
import { RoomChatMessages, SendMessagePayload } from "@lifeat/shared/rooms/room-chat/RoomChatTypes";
import * as ArgumentUtil from "@lifeat/shared/util/ArgumentUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { db, functions } from "@firebase-local/firebase";
import { ROOM_CHAT_FUNCTIONS } from "@firebase-local/functions/ServerlessEndpoints";
import { SendRoomChatMessage } from "./AmplitudeRoomChatEvents";
import { TASK_CHECKED_EMOJI, TASK_UNCHECKED_EMOJI } from "./RoomChatConstants";

const amplitudeService = AmplitudeService.getInstance();

/**
 * Sends a chat message when in a Room.
 * @param {SendMessagePayload} messagePayload Data needed to send the message.
 * @param {() => void} onErrorCallback Callback function to call on error.
 * Author UID is provided by Callable Auth Context.
 */
export function sendRoomChatMessage(messagePayload: SendMessagePayload, onErrorCallback?: () => void) {
    if (messagePayload.messageText.length <= CHAT_MESSAGE_LENGTH_LIMIT) {
        const messageIsTaskList =
            messagePayload.messageText.includes(TASK_UNCHECKED_EMOJI) ||
            messagePayload.messageText.includes(TASK_CHECKED_EMOJI);
        functions
            .httpsCallable(ROOM_CHAT_FUNCTIONS.SendMessage)(messagePayload)
            .then(() => {
                GA4Service.logEvent(GA4Event.SEND_ROOM_CHAT_MESSAGE, {
                    [GA4EventProperties.rooms_info_id]: messagePayload.roomId,
                });
                amplitudeService.logEvent(
                    new SendRoomChatMessage(messagePayload.roomId, messagePayload.isRoomHost, messageIsTaskList),
                );
            })
            .catch(() => {
                alert("There was an error sending the message. Please try again or contact support.");
                onErrorCallback?.();
            });
    } else {
        alert("Sorry your message is too long to send.");
    }
}

/**
 * Delete all messages in the Room Chat (as host)
 * @param {string} roomId Room ID messages are being deleted for.
 */
export async function deleteAllRoomChatMessages(roomId: string) {
    const payload: ClearAllMessagePayload = { roomId: roomId };
    return functions
        .httpsCallable(ROOM_CHAT_FUNCTIONS.DeleteAllMessages)(payload)
        .catch((e) => {
            alert(`There was an error deleting all chat messages. Error: ${e}`);
        });
}

/**
 * Report a message for violating guidelines.
 * @param {string} roomId The room that the message was sent in
 * @param {string} messageId The message id/key (created by Firebase)
 */
export function reportMessage(roomId: string, messageId: string) {
    const payload: ReportMessagePayload = { roomId: roomId, messageId: messageId };
    functions
        .httpsCallable(ROOM_CHAT_FUNCTIONS.ReportMessage)(payload)
        .catch((e) => {
            alert(`There was an error reporting the message. Error: ${e}`);
        });
}

/**
 * Delete a message.
 * @param {string} roomId The room that the message was sent in
 * @param {string} messageId The message id/key (created by Firebase)
 */
export async function deleteMessage(roomId: string, messageId: string) {
    const payload: DeleteMessagePayload = { roomId: roomId, messageId: messageId };
    return functions
        .httpsCallable(ROOM_CHAT_FUNCTIONS.DeleteMessage)(payload)
        .catch((e) => {
            alert(`There was an error deleting the message. Error: ${e}`);
        });
}

/**
 * Updates a pinned message based on the message ID.
 * @param {string} roomId The room that the message was sent in
 * @param {string | null} messageId The message id/key (created by
 *  Firebase) of the message. If null, any currently pinned message
 *  will be unpinned.
 */
export function updatePinnedMessage(roomId: string, messageId: string | null) {
    ArgumentUtil.validateRoomId(roomId);
    if (messageId !== null) {
        ArgumentUtil.validateString(messageId);
    }
    const pinnedMessagePath = `/${ROOM_CHATS_NODE.name}/${roomId}/${ROOM_CHATS_NODE.children.PINNED_MESSAGE}`;
    db.ref(pinnedMessagePath)
        .set(messageId)
        .catch((error) => {
            alert(`Unable to pin message. Error: ${error}`);
        });
}

export function getPinnedMessagesRef(roomId: string, messageId: string) {
    ArgumentUtil.validateRoomId(roomId);
    ArgumentUtil.validateString(messageId);
    return db.ref(`/${ROOM_CHATS_NODE.name}/${roomId}/${ROOM_CHATS_NODE.children.MESSAGES.name}/${messageId}`);
}

export function getRoomChatMessagesRef(roomId: string) {
    ArgumentUtil.validateRoomId(roomId);
    return db.ref(`/${ROOM_CHATS_NODE.name}/${roomId}/${ROOM_CHATS_NODE.children.MESSAGES.name}`);
}

export function getLatestChatMessagesRef(roomId: string, numMessages: number = MESSAGES_TO_FETCH_PAGE_SIZE) {
    return getRoomChatMessagesRef(roomId).limitToLast(numMessages);
}

/**
 * Checks to see if older messages exist that can be fetched.
 * @param {string} roomId Room that is being checked for more messages.
 * @param {string} latestMessageId The oldest message ID that the user has
 * fetched and is displayed in chat at the time this is called.
 * @returns {boolean} If there are more messages to be loaded.
 */
export async function checkIfMessagesExistToBeLoaded(roomId: string, latestMessageId: string): Promise<boolean> {
    // @ts-ignore Compiler is expecting return type to be string | boolean | null
    return await getRoomChatMessagesRef(roomId)
        .endBefore(latestMessageId)
        .limitToFirst(1) // only need to get the first message in the DB
        .once("value", (snapshot) => {
            if (snapshot.exists()) {
                return snapshot.val();
            }
            return undefined;
        })
        .then((snapshot) => {
            if (snapshot && snapshot.exists()) {
                const msgs = snapshot.val() as RoomChatMessages;
                const key = Object.keys(msgs)[0];
                // Lexicographic comparison.
                return key && key < latestMessageId;
            }
            return false;
        })
        .catch((e) => {
            alert(`There was an error checking for more messages. Error: ${e}`);
            return false;
        });
}
